import { isEmpty, isNil, sortBy, get, isEqual, isArray } from 'lodash-es';
import { subDays, endOfDay, subMonths, getYear, getMonth } from 'date-fns';
import Vue from 'vue';

import { companyService, invoiceService, activityService, customerService } from '@services';

const startOfDayUtc = () => {
  const now = new Date();

  return Date.UTC(now.getUTCFullYear(), now.getMonth(), now.getDate());
};

const endOfPreviousDayUtc = () => {
  const now = new Date();

  return Date.UTC(now.getUTCFullYear(), now.getMonth(), now.getDate()) - 1;
};

const startOfPreviousMonthUtc = () => {
  const now = new Date();

  return Date.UTC(now.getUTCFullYear(), now.getMonth() - 1, 1);
};

const endOfDayUtc = (timestamp) => {
  const date = new Date(timestamp);

  return endOfDay(date).getTime();
};

const startOfMonthUtc = () => {
  const now = new Date();

  return Date.UTC(now.getUTCFullYear(), now.getMonth(), 1);
};

const startOfTwoMonthsAgoUtc = () => {
  const now = new Date();

  return Date.UTC(now.getUTCFullYear(), now.getMonth() - 2, 1);
};

const startOfXMonthsAgoUtc = (x) => {
  const now = new Date();

  return Date.UTC(now.getUTCFullYear(), now.getMonth() - x, 1);
};

const thirtyDaysAgo = () => subDays(startOfDayUtc(), 30).getTime();

const sevenDaysAgo = () => subDays(startOfDayUtc(), 7).getTime();

const lastDayOfPreviousMonth = () => {
  const now = new Date();

  return Date.UTC(now.getFullYear(), now.getMonth(), 1) - 1;
};

const lastDayOfMonth = () => {
  const now = new Date();

  return Date.UTC(now.getFullYear(), now.getMonth() + 1, 1) - 1;
};

const previousMonth = () => [startOfPreviousMonthUtc(), lastDayOfPreviousMonth()];

const currentMonth = () => [startOfMonthUtc(), lastDayOfMonth()];

const getLastMonth = () => {
  const lastMonth = subMonths(new Date(), 1);

  return getMonth(lastMonth) + 1;
};

const getLastMonthYear = () => {
  const lastMonth = subMonths(new Date(), 1);

  return getYear(lastMonth);
};

const getMetricPercentage = ({ currentValue, pastValue }) => {
  try {
    if (!isEqual(pastValue, 0)) {
      const total = currentValue - pastValue;

      return (total / pastValue) * 100;
    }

    return 0;
  } catch ({ message }) {
    console.error(message);
  }
};

const getSessionFilters = (id) => {
  const timeFilterSession = sessionStorage[`timeFilter-${Vue.auth.user()?.selectedCompany?.id}-${Vue.auth.user().id}`];

  if (!isNil(timeFilterSession) && !isEmpty(timeFilterSession) && !isEqual(timeFilterSession, 'undefined')) {
    const timeFilterSessionObject = JSON.parse(timeFilterSession);
    const filter = timeFilterSessionObject[id];

    if (!isNil(filter) && !isEmpty(filter)) {
      switch (filter) {
        case 'lastThirtyDays':
          return [thirtyDaysAgo(), endOfPreviousDayUtc()];
        case 'lastSevenDays':
          return [sevenDaysAgo(), endOfPreviousDayUtc()];
        default:
          if (isArray(filter) && !isEmpty(filter)) {
            return filter;
          }

          return [thirtyDaysAgo(), endOfPreviousDayUtc()];
      }
    } else {
      return [thirtyDaysAgo(), endOfPreviousDayUtc()];
    }
  }
};

export default {
  fetchMonthlyDSO: async ({ commit }, payload) => {
    try {
      const { customerId } = payload;

      const res = !isNil(customerId)
        ? await customerService.fetchMetrics(payload)
        : await companyService.fetchMetricMeasurements(payload);

      if (!isEmpty(res)) {
        const { value, totalAr, totalSales } = res[0];

        commit('SET_DSO', { value, totalAr, totalSales });
      } else {
        commit('SET_DSO', { value: 'N/A' });
      }
    } catch ({ message }) {
      console.error(message);
    }
  },
  fetchMonthlyCEI: async ({ commit }, payload) => {
    try {
      const res = await companyService.fetchMetricMeasurements(payload);

      if (!isEmpty(res)) {
        commit('SET_CEI', res[0].value);
      } else {
        commit('SET_CEI', 'N/A');
      }
    } catch ({ message }) {
      console.error(message);
    }
  },
  fetchMonthlyDSOStandard: async ({ commit }, payload) => {
    try {
      const res = await companyService.fetchMetricMeasurements(payload);

      if (!isEmpty(res)) {
        const { value, totalAr, totalSales } = res[0];

        commit('SET_DSO_STANDARD', { value, totalAr, totalSales });
      } else {
        commit('SET_DSO_STANDARD', { value: 'N/A' });
      }
    } catch ({ message }) {
      console.error(message);
    }
  },
  fetchMonthlyADD: async ({ commit }, payload) => {
    try {
      const res = await companyService.fetchMetricMeasurements(payload);

      if (!isEmpty(res)) {
        commit('SET_ADD', res[0]);
      } else {
        commit('SET_ADD', { value: 'N/A' });
      }
    } catch ({ message }) {
      console.error(message);
    }
  },
  fetchMonthlyMDD: async ({ commit }, payload) => {
    try {
      const { customerId } = payload;

      const res = !isNil(customerId)
        ? await customerService.fetchMetrics(payload)
        : await companyService.fetchMetricMeasurements(payload);

      if (!isEmpty(res)) {
        commit('SET_MDD', res[0]);
      } else {
        commit('SET_MDD', { value: 'N/A' });
      }
    } catch ({ message }) {
      console.error(message);
    }
  },
  fetchTrend: async ({ commit }, payload) => {
    try {
      const dateRange = [thirtyDaysAgo(), startOfDayUtc()];

      const [pastValue, currentValue] = await Promise.all([
        companyService.fetchMetric({
          ...payload,
          dateRange: undefined,
          timestamp: dateRange[0],
        }),
        companyService.fetchMetric({
          ...payload,
          dateRange: undefined,
          timestamp: dateRange[1],
        }),
      ]);

      if (!isEmpty(pastValue) && !isEmpty(currentValue)) {
        commit('SET_TREND', {
          name: payload.name,
          value: getMetricPercentage({
            pastValue: pastValue[0].value,
            currentValue: currentValue[0].value,
          }),
        });
      } else {
        commit('SET_TREND', {
          name: payload.name,
          value: undefined,
        });
      }
    } catch ({ message }) {
      console.error(message);
    }
  },
  fetchDSOTrend: async ({ commit }, payload) => {
    try {
      const { customerId } = payload;

      let values = !isNil(customerId)
        ? await customerService.fetchMetrics(payload)
        : await companyService.fetchMetricMeasurements(payload);

      if (!isEmpty(values)) {
        values = sortBy(values, 'timestamp');

        commit('SET_TREND', {
          name: 'dso',
          value: getMetricPercentage({
            pastValue: get(values[0], 'value', 0),
            currentValue: get(values[1], 'value', 0),
          }),
        });
      } else {
        commit('SET_TREND', {
          name: 'dso',
          value: undefined,
        });
      }
    } catch ({ message }) {
      console.error(message);
    }
  },
  fetchDSOStandardTrend: async ({ commit }, payload) => {
    try {
      let values = await companyService.fetchMetricMeasurements(payload);

      if (!isEmpty(values)) {
        values = sortBy(values, 'timestamp');

        commit('SET_TREND', {
          name: 'dsoStandard',
          value: getMetricPercentage({
            pastValue: get(values[0], 'value', 0),
            currentValue: get(values[1], 'value', 0),
          }),
        });
      } else {
        commit('SET_TREND', {
          name: 'dsoStandard',
          value: undefined,
        });
      }
    } catch ({ message }) {
      console.error(message);
    }
  },
  fetchMDDTrend: async ({ commit }, payload) => {
    try {
      const { customerId } = payload;

      let values = !isNil(customerId)
        ? await customerService.fetchMetrics(payload)
        : await companyService.fetchMetricMeasurements(payload);

      if (!isEmpty(values)) {
        values = sortBy(values, 'timestamp');

        commit('SET_TREND', {
          name: 'mdd',
          value: getMetricPercentage({
            pastValue: get(values[0], 'value', 0),
            currentValue: get(values[1], 'value', 0),
          }),
        });
      } else {
        commit('SET_TREND', {
          name: 'mdd',
          value: undefined,
        });
      }
    } catch ({ message }) {
      console.error(message);
    }
  },
  fetchADDTrend: async ({ commit }, payload) => {
    try {
      let values = await companyService.fetchMetricMeasurements(payload);

      if (!isEmpty(values)) {
        values = sortBy(values, 'timestamp');

        commit('SET_TREND', {
          name: 'add',
          value: getMetricPercentage({
            pastValue: get(values[0], 'value', 0),
            currentValue: get(values[1], 'value', 0),
          }),
        });
      } else {
        commit('SET_TREND', {
          name: 'add',
          value: undefined,
        });
      }
    } catch ({ message }) {
      console.error(message);
    }
  },
  fetchCollectionRateTrend: async ({ commit }, payload) => {
    try {
      const { customerId } = payload;

      let values = !isNil(customerId)
        ? await customerService.fetchMetrics(payload)
        : await companyService.fetchMetricMeasurements(payload);

      if (!isEmpty(values)) {
        values = sortBy(values, 'timestamp');

        commit('SET_TREND', {
          name: 'collectionRate',
          value: getMetricPercentage({
            pastValue: get(values[0], 'value', 0),
            currentValue: get(values[1], 'value', 0),
          }),
        });
      } else {
        commit('SET_TREND', {
          name: 'collectionRate',
          value: undefined,
        });
      }
    } catch ({ message }) {
      console.error(message);
    }
  },
  fetchClosedInvoicesData: async ({ commit }, payload) => {
    try {
      const { closedInvoicesTotalAmount } = await invoiceService.fetchCountClosed(payload);

      commit('SET_CLOSED_INVOICES_TOTAL_AMOUNT', !isNil(closedInvoicesTotalAmount) ? closedInvoicesTotalAmount : 0);
    } catch ({ message }) {
      console.error(message);
    }
  },
  fetchWorkflowsActivitiesCount: async ({ commit }, payload) => {
    try {
      const { workflowActivities } = await activityService.count(payload);

      commit('SET_WORKFLOW_ACTIVITIES', workflowActivities);
    } catch ({ message }) {
      console.error(message);
    }
  },
  fetchNotesActivitiesCount: async ({ commit }, payload) => {
    try {
      const { notesActivities } = await activityService.count(payload);

      commit('SET_NOTES_ACTIVITIES', notesActivities);
    } catch ({ message }) {
      console.error(message);
    }
  },
  fetchManualNotesActivitiesCount: async ({ commit }, payload) => {
    try {
      const { manualNotesActivities } = await activityService.count(payload);

      commit('SET_MANUAL_NOTES_ACTIVITIES', manualNotesActivities);
    } catch ({ message }) {
      console.error(message);
    }
  },
  fetchMonthlyCollectionRate: async ({ commit }, payload) => {
    try {
      const { customerId } = payload;

      const res = !isNil(customerId)
        ? await customerService.fetchMetrics(payload)
        : await companyService.fetchMetricMeasurements(payload);

      if (!isEmpty(res)) {
        commit('SET_COLLECTION_RATE', res[0]);
      } else {
        commit('SET_COLLECTION_RATE', 'N/A');
      }
    } catch ({ message }) {
      console.error(message);
    }
  },
  fetchManualTimeSaved: async ({ commit }) => {
    try {
      const [res] = await companyService.fetchMetric({
        companyId: Vue.auth.user()?.selectedCompany?.id,
        timestamp: Date.now(),
        name: 'manualTimeSaved',
      });

      if (!isEmpty(res) && !isEmpty(res?.value)) {
        commit('SET_MANUAL_TIME_SAVED', res?.value);
      }
    } catch ({ message }) {
      console.error(message);
    }
  },
  getAgingBucketsWidget: async ({ commit, dispatch }, { companyId, users, search }) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: 'agingBuckets',
        value: true,
      });

      await dispatch(
        'charts/fetchAgingBucketsChart',
        {
          companyId,
          users,
          search,
        },
        { root: true },
      );
    } catch ({ message }) {
      console.error(message);
    } finally {
      commit('SET_LOADING_WIDGETS', {
        id: 'agingBuckets',
        value: false,
      });
    }
  },
  getTermsOfPaymentsWidget: async ({ commit, dispatch }, { companyId, search, users }) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: 'termsOfPayments',
        value: true,
      });

      await dispatch(
        'charts/fetchTermsOfPaymentsChart',
        {
          name: 'termsOfPayments',
          companyId,
          periodType: 'monthly',
          search,
          users,
          dateRange: [startOfPreviousMonthUtc(), startOfPreviousMonthUtc()],
        },
        { root: true },
      );
    } catch ({ message }) {
      console.error(message);
    } finally {
      commit('SET_LOADING_WIDGETS', {
        id: 'termsOfPayments',
        value: false,
      });
    }
  },
  getTotalARWidget: async ({ commit, dispatch }, { companyId, users, search }) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: 'totalAR',
        value: true,
      });

      await Promise.all([
        dispatch(
          'invoices/fetchTotalARBalance',
          {
            companyId,
            users,
            search,
          },
          { root: true },
        ),
        dispatch('fetchTrend', {
          companyId,
          name: 'totalAr',
        }),
      ]);
    } catch ({ message }) {
      console.error(message);
    } finally {
      commit('SET_LOADING_WIDGETS', {
        id: 'totalAR',
        value: false,
      });
    }
  },
  getTotalDisputedARWidget: async ({ commit, dispatch }, { companyId, users, search }) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: 'disputesTotalAr',
        value: true,
      });

      await Promise.all([
        dispatch(
          'invoices/fetchTotalDisputedARBalance',
          {
            companyId,
            users,
            search,
          },
          { root: true },
        ),
        dispatch('fetchTrend', {
          companyId,
          name: 'disputesTotalAr',
        }),
      ]);
    } catch ({ message }) {
      console.error(message);
    } finally {
      commit('SET_LOADING_WIDGETS', {
        id: 'disputesTotalAr',
        value: false,
      });
    }
  },
  getCustomerCreditLimitWidget: async ({ commit, dispatch }, { companyId, users, search }) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: 'customersCreditLimit',
        value: true,
      });

      const [creditLimit] = await Promise.all([
        customerService.fetchCustomersCreditLimit({
          companyId,
          users,
          search,
        }),
        dispatch('fetchTrend', {
          companyId,
          name: 'customersCreditLimit',
        }),
      ]);

      commit('SET_CUSTOMERS_CREDIT_LIMIT', creditLimit);
    } catch ({ message }) {
      console.error(message);
    } finally {
      commit('SET_LOADING_WIDGETS', {
        id: 'customersCreditLimit',
        value: false,
      });
    }
  },
  getOutstandingInvoicesWidget: async ({ commit, dispatch }, { companyId, users, search }) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: 'openInvoices',
        value: true,
      });

      const [outstandingInvoices] = await Promise.all([
        invoiceService.fetchCountOutstanding({
          companyId,
          users,
          search,
        }),
        dispatch('fetchTrend', {
          companyId,
          name: 'countOutstandingInvoices',
        }),
      ]);

      commit('SET_OUTSTANDING_INVOICES', outstandingInvoices);
    } catch ({ message }) {
      console.error(message);
    } finally {
      commit('SET_LOADING_WIDGETS', {
        id: 'openInvoices',
        value: false,
      });
    }
  },
  getOutstandingInvoicesChart: async ({ commit, dispatch }, { companyId, users, search }) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: 'openInvoicesChart',
        value: true,
      });

      await dispatch(
        'charts/fetchOutstandingInvoicesChart',
        {
          companyId,
          users,
          search,
        },
        { root: true },
      );
    } catch ({ message }) {
      console.error(message);
    } finally {
      commit('SET_LOADING_WIDGETS', {
        id: 'openInvoicesChart',
        value: false,
      });
    }
  },
  getPaymentGatewayChart: async ({ commit, dispatch }, { companyId, dashboardSearch, users }) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: 'paymentGatewayChart',
        value: true,
      });

      await dispatch(
        'charts/fetchPaymentGatewayChart',
        {
          companyId,
          dateRange: [startOfXMonthsAgoUtc(3), Date.now()],
          dashboardSearch,
          users,
        },
        { root: true },
      );
    } catch ({ message }) {
      console.error(message);
    } finally {
      commit('SET_LOADING_WIDGETS', {
        id: 'paymentGatewayChart',
        value: false,
      });
    }
  },
  getPaymentsHistoryChart: async ({ commit, dispatch }, { search, users }) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: 'paymentsHistoryChart',
        value: true,
      });

      await dispatch(
        'charts/fetchPaymentsHistoryChart',
        {
          companyId: Vue.auth.user()?.selectedCompany?.id,
          dateRange: [startOfXMonthsAgoUtc(3), Date.now()],
          users,
          search,
        },
        { root: true },
      );
    } catch ({ message }) {
      console.error(message);
    } finally {
      commit('SET_LOADING_WIDGETS', {
        id: 'paymentsHistoryChart',
        value: false,
      });
    }
  },
  getOutstandingInvoicesAmountChart: async ({ commit, dispatch }, { companyId, users, search }) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: 'openInvoicesAmountChart',
        value: true,
      });

      await dispatch(
        'charts/fetchOutstandingInvoicesAmountChart',
        {
          companyId,
          users,
          search,
        },
        { root: true },
      );
    } catch ({ message }) {
      console.error(message);
    } finally {
      commit('SET_LOADING_WIDGETS', {
        id: 'openInvoicesAmountChart',
        value: false,
      });
    }
  },
  getCreditLimitUsageChart: async ({ commit, dispatch }, payload) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: 'creditLimitUsage',
        value: true,
      });

      await dispatch('charts/setCreditLimitUsageChart', payload, { root: true });
    } catch ({ message }) {
      console.error(message);
    } finally {
      commit('SET_LOADING_WIDGETS', {
        id: 'creditLimitUsage',
        value: false,
      });
    }
  },
  getCustomersRiskChart: async ({ commit, dispatch }, { companyId, users, search }) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: 'customersRiskChart',
        value: true,
      });

      await dispatch(
        'charts/fetchCustomerRiskChart',
        {
          companyId,
          users,
          search,
        },
        { root: true },
      );
    } catch ({ message }) {
      console.error(message);
    } finally {
      commit('SET_LOADING_WIDGETS', {
        id: 'customersRiskChart',
        value: false,
      });
    }
  },
  getDSOWidget: async ({ commit, dispatch }, { companyId, customerId }) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: 'dso',
        value: true,
      });

      await Promise.all([
        dispatch('fetchMonthlyDSO', {
          name: 'dso',
          companyId,
          periodType: 'monthly',
          customerId,
          dateRange: [startOfPreviousMonthUtc(), endOfDayUtc(startOfPreviousMonthUtc())],
          month: getLastMonth(),
          year: getLastMonthYear(),
        }),
        dispatch('fetchDSOTrend', {
          name: 'dso',
          companyId,
          customerId,
          periodType: 'monthly',
          dateRange: [startOfTwoMonthsAgoUtc(), endOfDayUtc(startOfPreviousMonthUtc())],
        }),
      ]);
    } catch ({ message }) {
      console.error(message);
    } finally {
      commit('SET_LOADING_WIDGETS', {
        id: 'dso',
        value: false,
      });
    }
  },
  getCEIWidget: async ({ commit, dispatch }, { companyId }) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: 'cei',
        value: true,
      });

      await Promise.all([
        dispatch('fetchMonthlyCEI', {
          name: 'cei',
          companyId,
          periodType: 'monthly',
          dateRange: [startOfPreviousMonthUtc(), startOfPreviousMonthUtc()],
        }),
      ]);
    } catch ({ message }) {
      console.error(message);
    } finally {
      commit('SET_LOADING_WIDGETS', {
        id: 'cei',
        value: false,
      });
    }
  },
  getDSOStandardWidget: async ({ commit, dispatch }, { companyId }) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: 'dsoStandard',
        value: true,
      });

      await Promise.all([
        dispatch('fetchMonthlyDSOStandard', {
          name: 'dsoStandard',
          companyId,
          periodType: 'monthly',
          dateRange: [startOfPreviousMonthUtc(), startOfPreviousMonthUtc()],
        }),
        dispatch('fetchDSOStandardTrend', {
          name: 'dsoStandard',
          companyId,
          periodType: 'monthly',
          dateRange: [startOfTwoMonthsAgoUtc(), startOfPreviousMonthUtc()],
        }),
      ]);
    } catch ({ message }) {
      console.error(message);
    } finally {
      commit('SET_LOADING_WIDGETS', {
        id: 'dsoStandard',
        value: false,
      });
    }
  },
  getADDWidget: async ({ commit, dispatch }, { companyId }) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: 'add',
        value: true,
      });

      await Promise.all([
        dispatch('fetchMonthlyADD', {
          name: 'add',
          companyId,
          periodType: 'monthly',
          dateRange: [startOfPreviousMonthUtc(), startOfPreviousMonthUtc()],
        }),
        dispatch('fetchADDTrend', {
          name: 'add',
          companyId,
          periodType: 'monthly',
          dateRange: [startOfTwoMonthsAgoUtc(), startOfPreviousMonthUtc()],
        }),
      ]);
    } catch ({ message }) {
      console.error(message);
    } finally {
      commit('SET_LOADING_WIDGETS', {
        id: 'add',
        value: false,
      });
    }
  },
  getMDDWidget: async ({ commit, dispatch }, { companyId, customerId }) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: 'mdd',
        value: true,
      });

      await Promise.all([
        dispatch('fetchMonthlyMDD', {
          name: 'mdd',
          companyId,
          customerId,
          periodType: 'monthly',
          dateRange: [startOfPreviousMonthUtc(), endOfDayUtc(startOfPreviousMonthUtc())],
          month: getLastMonth(),
          year: getLastMonthYear(),
        }),
        dispatch('fetchMDDTrend', {
          name: 'mdd',
          companyId,
          customerId,
          periodType: 'monthly',
          dateRange: [startOfTwoMonthsAgoUtc(), endOfDayUtc(startOfPreviousMonthUtc())],
        }),
      ]);
    } catch ({ message }) {
      console.error(message);
    } finally {
      commit('SET_LOADING_WIDGETS', {
        id: 'mdd',
        value: false,
      });
    }
  },
  getTopCustomersWidget: async (
    { commit, dispatch },
    { companyId, users, search, recordsNumber, contentType, headers },
  ) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: 'topDebtors',
        value: true,
      });

      await dispatch(
        'customers/fetchCustomersMetrics',
        {
          companyId,
          users,
          type: 'top',
          sort: { 'stats.totalLocalBalance': -1 },
          page: 1,
          perPage: recordsNumber,
          search: search.customer,
          contentType,
          headers,
        },
        { root: true },
      );
    } catch ({ message }) {
      console.error(message);
    } finally {
      commit('SET_LOADING_WIDGETS', {
        id: 'topDebtors',
        value: false,
      });
    }
  },
  getDisputedCustomersWidget: async (
    { commit, dispatch },
    { companyId, users, search, recordsNumber, contentType, headers },
  ) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: 'disputedCustomers',
        value: true,
      });

      await dispatch(
        'customers/fetchCustomersMetrics',
        {
          companyId,
          users,
          type: 'disputed',
          sort: { 'stats.disputesTotalLocalAmount': -1 },
          page: 1,
          perPage: recordsNumber,
          search: { stats: { disputesOpenCount: [1, 1000000000] }, ...search.customer },
          contentType,
          headers,
        },
        { root: true },
      );
    } catch ({ message }) {
      console.error(message);
    } finally {
      commit('SET_LOADING_WIDGETS', {
        id: 'disputedCustomers',
        value: false,
      });
    }
  },
  getOverdueCustomersWidget: async (
    { commit, dispatch },
    { companyId, users, search, recordsNumber, contentType, headers },
  ) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: 'pastDueCustomers',
        value: true,
      });

      await Promise.all([
        dispatch(
          'customers/fetchCustomersMetrics',
          {
            companyId,
            users,
            type: 'overdue',
            sort: { 'stats.overdueLocalBalance': -1 },
            page: 1,
            perPage: recordsNumber,
            search: search.customer,
            contentType,
            headers,
          },
          { root: true },
        ),
        dispatch(
          'customers/fetchCountOverdueCustomers',
          {
            companyId,
            search,
          },
          { root: true },
        ),
        dispatch('fetchTrend', {
          companyId,
          name: 'countCustomersOverdue',
        }),
      ]);
    } catch ({ message }) {
      console.error(message);
    } finally {
      commit('SET_LOADING_WIDGETS', {
        id: 'pastDueCustomers',
        value: false,
      });
    }
  },
  getClosedInvoicesData: async ({ commit, dispatch }, { companyId, users, period, search }) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: 'closedInvoices',
        value: true,
      });

      await dispatch('fetchClosedInvoicesData', {
        companyId,
        period,
        users,
        search,
      });
    } catch ({ message }) {
      console.error(message);
    } finally {
      commit('SET_LOADING_WIDGETS', {
        id: 'closedInvoices',
        value: false,
      });
    }
  },
  getWorkflowsActivitiesCount: async ({ commit, dispatch }, { companyId, users, period, search }) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: 'workflowsActivities',
        value: true,
      });

      await dispatch('fetchWorkflowsActivitiesCount', {
        companyId,
        period,
        users,
        search,
      });
    } catch ({ message }) {
      console.error(message);
    } finally {
      commit('SET_LOADING_WIDGETS', {
        id: 'workflowsActivities',
        value: false,
      });
    }
  },
  getNotesActivitiesCount: async ({ commit, dispatch }, { companyId, users, period, search }) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: 'notesActivities',
        value: true,
      });

      await dispatch('fetchNotesActivitiesCount', {
        companyId,
        period,
        users,
        search,
      });
    } catch ({ message }) {
      console.error(message);
    } finally {
      commit('SET_LOADING_WIDGETS', {
        id: 'notesActivities',
        value: false,
      });
    }
  },
  getManualNotesActivitiesCount: async ({ commit, dispatch }, { companyId, users, period, search }) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: 'manualNotesActivities',
        value: true,
      });

      await dispatch('fetchManualNotesActivitiesCount', {
        companyId,
        period,
        users,
        search,
      });
    } catch ({ message }) {
      console.error(message);
    } finally {
      commit('SET_LOADING_WIDGETS', {
        id: 'manualNotesActivities',
        value: false,
      });
    }
  },
  getPaymentsForecastWidget: async ({ commit, dispatch }, { companyId, users, search }) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: 'payments',
        value: true,
      });

      await Promise.all([
        dispatch(
          'charts/fetchPastPayment',
          {
            companyId,
            periods: [previousMonth(), currentMonth()],
            users,
            search,
          },
          { root: true },
        ),
        dispatch(
          'charts/fetchPaymentForecasting',
          {
            companyId,
            users,
            search,
          },
          { root: true },
        ),
      ]);
    } catch ({ message }) {
      console.error(message);
    } finally {
      commit('SET_LOADING_WIDGETS', {
        id: 'payments',
        value: false,
      });
    }
  },
  delayPaymentForecastWidget: async ({ commit }) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: 'payments',
        value: true,
      });

      setTimeout(() => {
        commit('SET_LOADING_WIDGETS', {
          id: 'payments',
          value: false,
        });
      }, 1000);
    } catch ({ message }) {
      console.error(message);
    }
  },
  getCustomersChartWidget: async ({ commit, dispatch }, { companyId, users, UCRType, search }) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: 'customersChart',
        value: true,
      });

      await dispatch(
        'charts/fetchCustomersChart',
        {
          companyId,
          users,
          UCRType,
          sort: { 'stats.totalLocalBalance': -1 },
          page: 1,
          perPage: 100,
          search: search.customer,
        },
        { root: true },
      );
    } catch ({ message }) {
      console.error(message);
    } finally {
      commit('SET_LOADING_WIDGETS', {
        id: 'customersChart',
        value: false,
      });
    }
  },
  getInvoicesChartWidget: async ({ commit, dispatch }, { companyId, users, search }) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: 'invoicesChart',
        value: true,
      });

      await dispatch(
        'charts/fetchInvoicesChart',
        {
          companyId,
          users,
          sort: { 'stats.totalLocalBalance': -1 },
          page: 1,
          perPage: 100,
          search,
        },
        { root: true },
      );
    } catch ({ message }) {
      console.error(message);
    } finally {
      commit('SET_LOADING_WIDGETS', {
        id: 'invoicesChart',
        value: false,
      });
    }
  },
  setFilterWidgets: ({ commit }, { id, value }) => {
    commit('SET_FILTER_WIDGETS', {
      id,
      value,
    });
  },
  getRatesWidget: async ({ commit, dispatch }, { companyId, rate, country }) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: rate,
        value: true,
      });

      await dispatch('rates/fetchRates', { companyId, rate, country }, { root: true });
    } catch ({ message }) {
      console.error(message);
    } finally {
      commit('SET_LOADING_WIDGETS', {
        id: rate,
        value: false,
      });
    }
  },
  getCollectionRateWidget: async ({ commit, dispatch }, { companyId, customerId }) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: 'collectionRate',
        value: true,
      });

      await Promise.all([
        dispatch('fetchMonthlyCollectionRate', {
          name: 'collectionRate',
          companyId,
          periodType: 'monthly',
          customerId,
          dateRange: [startOfPreviousMonthUtc(), endOfDayUtc(startOfPreviousMonthUtc())],
          month: getLastMonth(),
          year: getLastMonthYear(),
        }),
        dispatch('fetchCollectionRateTrend', {
          name: 'collectionRate',
          companyId,
          periodType: 'monthly',
          customerId,
          dateRange: [startOfTwoMonthsAgoUtc(), endOfDayUtc(startOfPreviousMonthUtc())],
        }),
      ]);
    } catch ({ message }) {
      console.error(message);
    } finally {
      commit('SET_LOADING_WIDGETS', {
        id: 'collectionRate',
        value: false,
      });
    }
  },
  getManualTimeSavedWidget: async ({ commit, dispatch }) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: 'manualTimeSaved',
        value: true,
      });

      await dispatch('fetchManualTimeSaved');
    } catch ({ message }) {
      console.error(message);
    } finally {
      commit('SET_LOADING_WIDGETS', {
        id: 'manualTimeSaved',
        value: false,
      });
    }
  },
  getDisputesChartWidget: async ({ commit, dispatch }) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: 'disputesChart',
        value: true,
      });

      await dispatch('charts/fetchDisputesChart', null, { root: true });
    } catch ({ message }) {
      console.error(message);
    } finally {
      commit('SET_LOADING_WIDGETS', {
        id: 'disputesChart',
        value: false,
      });
    }
  },
  getCustomWidgetData: async ({ commit, dispatch }, payload) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: payload.id,
        value: true,
      });

      const period = getSessionFilters(payload.id);

      payload.period = isEmpty(payload.period) ? period : payload.period;

      await dispatch('customWidgets/getChartData', payload, { root: true });
    } catch ({ message }) {
      console.error(message);
    } finally {
      commit('SET_LOADING_WIDGETS', {
        id: payload.id,
        value: false,
      });
    }
  },
  getEmailStatusMetricsChart: async ({ commit, dispatch }, payload = {}) => {
    try {
      commit('SET_LOADING_WIDGETS', {
        id: 'emailStatusMetricsChart',
        value: true,
      });

      const period = getSessionFilters(payload.id);

      payload.period = isEmpty(payload.period) ? period : payload.period;

      await dispatch('charts/fetchEmailStatusMetricsChart', payload, { root: true });
    } catch ({ message }) {
      console.error(message);
    } finally {
      commit('SET_LOADING_WIDGETS', {
        id: 'emailStatusMetricsChart',
        value: false,
      });
    }
  },
  addLoadingWidgetState: ({ commit }, id) => {
    commit('ADD_LOADING_WIDGET_STATE', id);
  },
  addFilterWidgetState: ({ commit }, id) => {
    commit('ADD_FILTER_WIDGET_STATE', id);
  },
};
